import React from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material';
import FEditableGrid, { EditablePageField, FEditableGridColumn, FEditableGridPayload, FEditableGridRow } from '../../../components/egrid';

import DirectBudgetCardsPanel from './DirectBudgetCardsPanel';
import DirectBudgetRollupPanel from './DirectBudgetRollupPanel';
import { DirectBudgetCard, DirectBudgetRollupRow } from './types';
import { fetchDirectBudgetCards, fetchDirectBudgetRollup, fetchDirectBudgetRows, saveDirectBudgetRows } from './directBudgetApi';
import { createFieldObject, type EFieldConfig } from '../../../components/form';
import { formatDate } from '../../../components/renderers';

type State = {
  cycleCode: string;
  periodCode: string;
  costCenterCode: string;
  loading: boolean;
  error: string;
  cards: DirectBudgetCard | null;
  rows: FEditableGridRow[];
  rollup: DirectBudgetRollupRow[];
};

const gform: FEditableGridColumn[] = [
  { id: 'acc', label: 'Account', type: 'tf', req: 1, width: 140 },
  { id: 'nam', label: 'Name', type: 'tf', req: 1, width: 220 },
  { id: 'dsc', label: 'Description', type: 'tf', req: 0, width: 260 },
  { id: 'qty', label: 'Qty', type: 'nf', req: 1, width: 100, defaultValue: 1 },
  { id: 'upr', label: 'Unit Price', type: 'nf', req: 1, width: 130, defaultValue: 0 },
  { id: 'amt', label: 'Amount', type: 'nf', req: 1, width: 140, defaultValue: 0 },
  { id: 'rqa', label: 'Requested', type: 'nf', req: 0, width: 140, defaultValue: 0 },
  { id: 'apa', label: 'Approved', type: 'nf', req: 0, width: 140, defaultValue: 0 },
  { id: 'jus', label: 'Justification', type: 'tf', req: 1, width: 280 },
  { id: 'rmk', label: 'Remarks', type: 'tf', req: 0, width: 240 },
];

class DirectBudgetWorkbench extends React.Component<any, State> {
  state: State = {
    cycleCode: '2025',
    periodCode: '',
    costCenterCode: '',
    loading: false,
    error: '',
    cards: null,
    rows: [],
    rollup: [],
  };

  componentDidMount() {
    this.load();
  }

  formconfigs: { [name: string]: EFieldConfig } = {
    bcc: { id: 'bcc', label: 'Budget Cycle', type: 'fc', req: 1, width: 120, cp: { t: 'bcs', s: 'budgetcycle' } },
    bpc: { id: 'bpc', label: 'Period', type: 'fc', req: 0, width: 120, cp: { t: 'bcs', s: 'budgetperiod' } },
    ccc: { id: 'ccc', label: 'Cost Center', type: 'fc', req: 1, width: 150, cp: { t: 'bcs', s: 'costcenter' } },
  };

  makefield = (fieldconf: EFieldConfig): any => {
    const { service = 'directbudget', record = {} } = this.props;
    const mode = 'add';
    const formatters = { dob: formatDate };
    const onChange = this.onChange.bind(this);
    const extras = {};
    return createFieldObject(service, mode, fieldconf, record, formatters, onChange, extras);
  };

  onChange = (name: 'cycleCode' | 'periodCode' | 'costCenterCode') => (e: any) => {
    const raw = e?.target ? e.target.value : e;
    this.setState({ [name]: String(raw || '').toUpperCase() } as Pick<State, 'cycleCode' | 'periodCode' | 'costCenterCode'>);
  };

  filters = () => {
    const { cycleCode, periodCode, costCenterCode } = this.state;
    return {
      bcc: cycleCode || null,
      bpc: periodCode || null,
      ccc: costCenterCode || null,
    };
  };

  load = async () => {
    this.setState({ loading: true, error: '' });

    try {
      const filters = this.filters();
      const [cards, rows, rollup] = await Promise.all([
        fetchDirectBudgetCards(filters),
        fetchDirectBudgetRows(filters),
        fetchDirectBudgetRollup(filters),
      ]);

      this.setState({ cards, rows: rows as any, rollup });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  submit = async (payload: FEditableGridPayload) => {
    const filters = this.filters();
    if (!filters.ccc) throw new Error('Cost Center is required for direct budget save.');
    await saveDirectBudgetRows(filters, payload.rcd);
    await this.load();
  };

  render() {
    const { cycleCode, periodCode, costCenterCode, loading, error, cards, rows, rollup } = this.state;
    const { bcc: bccconf, bpc: bpcconf, ccc: cccconf } = this.formconfigs;
    const bccfield = this.makefield(bccconf);
    const bpcfield = this.makefield(bpcconf);
    const cccfield = this.makefield(cccconf);

    return (
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Direct Budget by Account</Typography>
            <Typography variant="body2" color="text.secondary">
              Budget recurrent, fixed or regulatory charges that do not originate from activities.
            </Typography>
          </Box>
          <Button variant="contained" onClick={this.load} disabled={loading}>Refresh</Button>
        </Stack>

        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <EditablePageField label="Budget Cycle" fieldobject={bccfield} value={cycleCode} onChange={this.onChange('cycleCode')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <EditablePageField label="Period" fieldobject={bpcfield} value={periodCode} onChange={this.onChange('periodCode')} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <EditablePageField label="Cost Center" fieldobject={cccfield} value={costCenterCode} onChange={this.onChange('costCenterCode')} fullWidth />
            </Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {loading && <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}><CircularProgress size={22} /><Typography>Loading direct budget...</Typography></Stack>}

        <Stack spacing={3}>
          <DirectBudgetCardsPanel cards={cards} />

          <Paper elevation={1} sx={{ p: 2 }}>
            <FEditableGrid
              title="Direct Budget Lines"
              gform={gform}
              value={rows}
              onChange={(rows) => this.setState({ rows })}
              onSubmit={this.submit}
              height={520}
            />
          </Paper>

          <DirectBudgetRollupPanel rows={rollup} />
        </Stack>
      </Box>
    );
  }
}

export default DirectBudgetWorkbench;
